"use client"

import Link from "next/link"
import { useLang } from "@/contexts/language-context"

/**
 * Términos del programa de referidos (spec 2026-10-07 §10). Bilingüe con
 * useLang().t(en, es). Las cifras y condiciones son las del encabezado fijo del
 * programa; no se prometen ingresos: el bono depende del restaurante.
 */
export default function TerminosClient() {
  const { t } = useLang()

  const tiers = [
    { range: t("$10,000 – $24,999", "$10,000 – $24,999"), pay: "$100" },
    { range: t("$25,000 – $49,999", "$25,000 – $49,999"), pay: "$250" },
    { range: t("$50,000 – $99,999", "$50,000 – $99,999"), pay: "$500" },
    { range: t("$100,000 or more", "$100,000 o más"), pay: "$1,000" },
  ]

  const sections: { title: string; body: string[]; list?: string[] }[] = [
    {
      title: t("1. Who can take part", "1. Quién puede participar"),
      body: [
        t(
          "Anyone with a valid US phone number can apply. Each person has one partner account and one code, no matter how many ways they find us.",
          "Puede aplicar cualquier persona con un teléfono válido de EE. UU. Cada persona tiene una sola cuenta de socio y un solo código, sin importar por cuántas puertas llegue."
        ),
        t(
          "We review every application by hand. Until we approve you, you are a pending partner: your code does not pay yet. We may approve, reject or ask for more information, and we do not have to explain a rejection.",
          "Revisamos cada solicitud a mano. Mientras no te aprobemos eres un socio pendiente: tu código todavía no paga. Podemos aprobar, rechazar o pedir más información, y no estamos obligados a explicar un rechazo."
        ),
        t(
          "You are an independent partner, not an employee, agent or representative of Redy. You cannot sign anything or make promises in our name.",
          "Eres un socio independiente, no empleado, agente ni representante de Redy. No puedes firmar nada ni hacer promesas en nuestro nombre."
        ),
      ],
    },
    {
      title: t("2. When a referral counts", "2. Cuándo cuenta un referido"),
      body: [
        t(
          "A restaurant counts as yours in one of two ways: when a visit with Redy is booked for it after you registered it, or when it signs up through your link (useredy.com/ref/YOURCODE).",
          "Un restaurante cuenta como tuyo de dos maneras: cuando se agenda su visita con Redy después de que lo registraste, o cuando se registra con tu link (useredy.com/ref/TUCÓDIGO)."
        ),
        t(
          "Registering a restaurant without a booked visit does not give you any right to a bonus, and the registration expires after 30 days. If several partners registered the same restaurant, the one whose visit is booked first gets the credit. Redy decides when the facts are unclear.",
          "Registrar un restaurante sin visita agendada no te da ningún derecho al bono, y el registro vence a los 30 días. Si varios socios registraron el mismo restaurante, cuenta el primero cuya visita se agende. Redy decide cuando los hechos no son claros."
        ),
        t(
          "A restaurant that already knew Redy before you touched it (it filled in a form, booked a demo or was already in our records earlier) does not count for you, or is held for review.",
          "Un restaurante que ya conocía Redy antes de que lo tocaras (llenó un formulario, agendó una demo o ya estaba en nuestros registros) no cuenta para ti, o queda en revisión."
        ),
        t(
          "You cannot refer yourself, your own business or a business you own or work for.",
          "No puedes referirte a ti mismo, ni a tu propio negocio, ni a uno que sea tuyo o donde trabajes."
        ),
      ],
    },
    {
      title: t("3. The 90 days and the limit on open visits", "3. Los 90 días y el tope de citas"),
      body: [
        t(
          "Once a visit is booked, the restaurant stays credited to you for 90 days from the date of that visit. If it signs up after those 90 days, it no longer counts for you.",
          "Cuando se agenda una visita, el restaurante queda acreditado a ti durante 90 días desde la fecha de esa visita. Si se registra después de esos 90 días, ya no cuenta para ti."
        ),
        t(
          "You can have up to 10 booked visits open at the same time (10 for ambassadors, foodies and referrers; 5 for owners and accountants). If the visit does not happen and is not rescheduled within 7 days, the restaurant goes back to registered status.",
          "Puedes tener hasta 10 citas abiertas a la vez para embajadores, foodies y referidores; 5 para dueños y contadores. Si la visita no se realiza y no se reagenda en 7 días, el restaurante vuelve al estado de registrado."
        ),
      ],
    },
    {
      title: t("4. What the restaurant must do, and the bonus", "4. Lo que debe cumplir el restaurante y el bono"),
      body: [
        t(
          "Your referral counts when a visit with Redy is booked or when the restaurant signs up with your link. The bonus is earned only if:",
          "Tu referido cuenta cuando se agenda su visita con Redy o cuando se registra con tu link. El bono se gana solo si:"
        ),
      ],
      list: [
        t("The restaurant is approved and starts selling by card with Redy.", "El restaurante es aprobado y empieza a vender con tarjeta con Redy."),
        t(
          "In its first full calendar month, or in the one after, it processes at least $10,000 by card. The bonus is the one for the first month that qualifies. We look at card volume net of refunds, and we do not count demo or sandbox sales.",
          "En su primer mes calendario completo, o en el siguiente, procesa al menos $10,000 con tarjeta. El bono es el del primer mes que cumpla. Miramos el volumen con tarjeta neto de reembolsos, y no contamos ventas de demostración ni de prueba."
        ),
        t(
          "There are at most two evaluations per restaurant. If the first qualifies, we do not look at the second. If neither qualifies, there is no bonus and we do not run a third.",
          "Hay como máximo dos evaluaciones por restaurante. Si la primera califica, no miramos la segunda. Si ninguna califica, no hay bono y no hacemos una tercera."
        ),
      ],
    },
    {
      title: t("5. The table", "5. La tabla"),
      body: [
        t(
          "Card volume in the month that qualifies → bonus: $10,000–$24,999 → $100 · $25,000–$49,999 → $250 · $50,000–$99,999 → $500 · $100,000 or more → $1,000. The most you can earn for one restaurant is $1,000.",
          "Volumen con tarjeta del mes que califica → bono: $10,000–$24,999 → $100 · $25,000–$49,999 → $250 · $50,000–$99,999 → $500 · $100,000 o más → $1,000. Lo máximo que puedes ganar por un restaurante es $1,000."
        ),
        t(
          "We do not promise that you will earn anything. There is no minimum and no guarantee: the bonus depends entirely on what the restaurant you introduce does.",
          "No prometemos que vayas a ganar algo. No hay mínimo ni garantía: el bono depende por completo de lo que haga el restaurante que presentas."
        ),
      ],
    },
    {
      title: t("6. 30 days of Pro for the restaurant", "6. 30 días de Pro para el restaurante"),
      body: [
        t(
          "A restaurant that comes in through the program gets 30 days of the Pro plan at no charge, counted from the day it starts selling, as long as it does not already have a paid plan or a courtesy plan. This benefit is for the restaurant; it does not reduce your bonus.",
          "Un restaurante que llega por el programa recibe 30 días del plan Pro sin costo, contados desde el día en que empieza a vender, siempre que no tenga ya un plan de pago o de cortesía. Este beneficio es para el restaurante; no reduce tu bono."
        ),
      ],
    },
    {
      title: t("7. How and when you are paid", "7. Cómo y cuándo se paga"),
      body: [
        t(
          "The bonus is paid within 30 days after the end of the month that qualifies, subject to approval and to your tax information being up to date. We pay by PayPal or Venmo, or by another method we agree on with you, to the account you give us.",
          "El bono se paga dentro de los 30 días siguientes al cierre del mes que califica, sujeto a aprobación y a tener tus datos fiscales al día. Pagamos por PayPal o Venmo, u otro método que acordemos contigo, a la cuenta que nos des."
        ),
        t(
          "If you change your payout account, the change takes effect 72 hours later, to protect you from someone else changing it.",
          "Si cambias tu cuenta de pago, el cambio se aplica 72 horas después, para protegerte de que alguien más la cambie."
        ),
        t(
          "We may hold a payment for review while we check it, for example when the restaurant had earlier contact with Redy, or while we wait for your tax form.",
          "Podemos retener un pago en revisión mientras lo comprobamos, por ejemplo si el restaurante tuvo contacto previo con Redy, o mientras esperamos tu formulario fiscal."
        ),
      ],
    },
    {
      title: t("8. Taxes", "8. Impuestos"),
      body: [
        t(
          "The bonus is taxable income and you are responsible for your own taxes. Before your payments in a calendar year reach $2,000, we need a W-9 from you; until we have it, we hold the payment that would take you past that amount. If we are required to report your payments, we will send you the corresponding tax form.",
          "El bono es ingreso gravable y eres responsable de tus propios impuestos. Antes de que tus pagos en un año calendario lleguen a $2,000, necesitamos un W-9 tuyo; mientras no lo tengamos, retenemos el pago que te haría pasar de esa cantidad. Si estamos obligados a reportar tus pagos, te enviaremos el formulario fiscal correspondiente."
        ),
      ],
    },
    {
      title: t("9. Accountants and other advisors", "9. Contadores y otros asesores"),
      body: [
        t(
          "If you are an accountant or advisor and you refer a client, you must tell that client in writing that you receive a commission from Redy for the referral, before or when you recommend Redy. Suggested wording: “I receive a commission from Redy if you sign up and meet its requirements.” You are responsible for complying with the rules of your profession and your state.",
          "Si eres contador o asesor y refieres a un cliente, debes informarle por escrito que recibes una comisión de Redy por la referencia, antes de recomendarle Redy o al momento de hacerlo. Texto sugerido: «Recibo una comisión de Redy si te registras y cumples sus requisitos». Eres responsable de cumplir las reglas de tu profesión y de tu estado."
        ),
      ],
    },
    {
      title: t("10. What you may and may not say", "10. Lo que puedes y no puedes decir"),
      body: [
        t(
          "Tell the truth about Redy and about the program. Do not promise a restaurant results we do not promise, do not promise yourself income, do not spam, and do not use misleading ads or fake reviews. If you share your link publicly, say clearly that you may earn a bonus if the restaurant signs up.",
          "Di la verdad sobre Redy y sobre el programa. No le prometas a un restaurante resultados que nosotros no prometemos, no te prometas ingresos a ti, no mandes spam y no uses anuncios engañosos ni reseñas falsas. Si compartes tu link en público, di con claridad que puedes ganar un bono si el restaurante se registra."
        ),
      ],
    },
    {
      title: t("11. Fraud and suspension", "11. Fraude y suspensión"),
      body: [
        t(
          "We may suspend or remove you, and cancel any payment that has not yet been sent, if we find fraud or abuse: fake or self-referred restaurants, fake sales, misleading promotion, or breaking these terms.",
          "Podemos suspenderte o retirarte, y cancelar cualquier pago que aún no se haya enviado, si encontramos fraude o abuso: restaurantes falsos o autorreferidos, ventas falsas, promoción engañosa o incumplimiento de estos términos."
        ),
      ],
    },
    {
      title: t("12. Changes to the program", "12. Cambios al programa"),
      body: [
        t(
          "We can change the table, the conditions or these terms. A change applies only going forward, and we give you 30 days’ notice before it takes effect.",
          "Podemos cambiar la tabla, las condiciones o estos términos. Un cambio aplica solo hacia adelante, y te avisamos con 30 días de anticipación antes de que entre en vigor."
        ),
      ],
    },
    {
      title: t("13. Disputes", "13. Disputas"),
      body: [
        t(
          "If you disagree about whether a restaurant counts for you or how much you are owed, write to us and we will review it. Redy decides, using our event log as the evidence of who registered what and when.",
          "Si no estás de acuerdo con que un restaurante cuente para ti o con lo que se te debe, escríbenos y lo revisamos. Decide Redy, usando nuestra bitácora de eventos como evidencia de quién registró qué y cuándo."
        ),
      ],
    },
  ]

  return (
    <main className="pt-36 pb-24 px-6">
      <div className="max-w-[720px] mx-auto">
        <div className="mb-12">
          <div className="text-[11px] font-bold tracking-widest uppercase text-redy-orange mb-4">
            {t("Referral program", "Programa de referidos")}
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight text-redy-charcoal mb-3">
            {t("Referral program terms", "Términos del programa de referidos")}
          </h1>
          <p className="text-[14px] font-medium text-redy-secondary">
            {t("In effect since October 15, 2026", "Vigentes desde el 15 de octubre de 2026")}
          </p>
          <p className="text-[14px] font-medium text-redy-secondary mt-1">
            {t(
              "Updated October 16, 2026: adds the $100,000-or-more tier → $1,000.",
              "Actualizados el 16 de octubre de 2026: se agrega el escalón de $100,000 o más → $1,000."
            )}
          </p>
        </div>

        <div className="bg-white border border-black/5 rounded-[32px] p-10 space-y-8 text-[15px] leading-relaxed text-redy-secondary">
          <p>
            {t(
              "Earn up to $1,000 for every restaurant you introduce to us. These are the rules of the program, run by Kickoff Business Solutions LLC (“Redy”). By checking the box on the sign-up form you accept them.",
              "Gana hasta $1,000 por cada restaurante que nos presentes. Estas son las reglas del programa, a cargo de Kickoff Business Solutions LLC («Redy»). Al marcar la casilla del formulario de inscripción las aceptas."
            )}
          </p>

          {sections.map((s) => (
            <section key={s.title}>
              <h2 className="text-[18px] font-black text-redy-charcoal mb-3">{s.title}</h2>
              <div className="space-y-3">
                {s.body.map((p) => (
                  <p key={p}>{p}</p>
                ))}
                {s.list ? (
                  <ol className="list-decimal pl-5 space-y-2">
                    {s.list.map((li) => (
                      <li key={li}>{li}</li>
                    ))}
                  </ol>
                ) : null}
              </div>
              {s.title.startsWith("5.") ? (
                <table className="mt-4 w-full text-left text-[14px] border border-black/10 rounded-xl overflow-hidden">
                  <thead className="bg-[#FAFAFA] text-redy-charcoal">
                    <tr>
                      <th className="px-4 py-2 font-bold">{t("Card volume in the qualifying month", "Volumen con tarjeta del mes que califica")}</th>
                      <th className="px-4 py-2 font-bold">{t("Bonus", "Bono")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tiers.map((row) => (
                      <tr key={row.range} className="border-t border-black/5">
                        <td className="px-4 py-2">{row.range}</td>
                        <td className="px-4 py-2 font-bold text-redy-charcoal">{row.pay}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
            </section>
          ))}

          <p>
            {t("Questions about the program? ", "¿Preguntas sobre el programa? ")}
            <Link href="/contact" className="underline underline-offset-2 text-redy-charcoal font-semibold">
              {t("Write to us", "Escríbenos")}
            </Link>
            {t(". See also our ", ". Mira también nuestra ")}
            <Link href="/privacy" className="underline underline-offset-2 text-redy-charcoal font-semibold">
              {t("Privacy Policy", "Política de privacidad")}
            </Link>
            .
          </p>
        </div>
      </div>
    </main>
  )
}
